import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import api from "@/lib/apiClient";
import { useSettings } from "@/context/SettingsContext";
import { isStatHidden } from "@/lib/siteNav";
import SEO from "@/components/SEO";
import PilRow from "@/components/PilRow";
import CoverageList from "@/components/CoverageList";
import AsSeenIn from "@/components/AsSeenIn";
import { SectionTitle } from "@/components/Shared";
import ImpactDashboard from "@/components/ImpactDashboard";
import HeroBackdrop from "@/components/HeroBackdrop";
import DomainsSection from "@/components/DomainsSection";
import LensStrip from "@/components/LensStrip";

export default function Home() {
  const s = useSettings();
  const [home, setHome] = useState(null);
  const [stats, setStats] = useState({});
  const [issues, setIssues] = useState([]);
  useEffect(() => {
    api.get("/public/home").then(({ data }) => setHome(data)).catch(() => {});
    api.get("/issues").then(({ data }) => setIssues(data)).catch(() => {});
    const loadStats = () => api.get("/public/stats").then(({ data }) => setStats(data)).catch(() => {});
    loadStats();
    const onVis = () => { if (document.visibilityState === "visible") loadStats(); };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  const statItems = [
    { id: "pils", label: "Public Interest Litigations", value: stats.pils },
    { id: "orders", label: "Court Orders Archived", value: stats.orders },
    { id: "public_representations", label: "Public Representations", value: stats.public_representations },
    { id: "areas_of_intervention", label: "Areas of Intervention", value: stats.areas_of_intervention },
    { id: "media_coverage", label: "Media Coverage", value: stats.media_coverage },
    { id: "documents", label: "Public Documents", value: stats.documents },
  ].filter((x) => x.value !== undefined && x.value !== null && x.value !== "" && !isStatHidden(s, x.id));

  return (
    <div className="fade-in">
      <SEO route="/" title={s.seo_title || "National Public Service Trust | NPST India"} description={s.meta_description} canonical={window.location.origin} jsonLd={{
        "@context": "https://schema.org",
        "@type": "NGO",
        "name": "National Public Service Trust",
        "alternateName": "NPST",
        "url": window.location.origin,
        "logo": `${window.location.origin}/npst-logo.webp`,
        "description": s.meta_description,
        ...(s.email ? { "email": s.email } : {}),
        ...(s.phone ? { "telephone": s.phone } : {}),
        ...(s.address ? { "address": { "@type": "PostalAddress", "streetAddress": s.address, "addressCountry": "IN" } } : {}),
      }} />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-npst-line paper-texture">
        <HeroBackdrop />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-24 lg:pt-28 lg:pb-32">
          <div className="max-w-4xl">
            <div className="label-eyebrow text-npst-gold mb-6" data-testid="hero-eyebrow">National Public Service Trust · Est. {s.established || "2024"}</div>
            <h1 className="font-serif text-4xl sm:text-6xl lg:text-7xl font-semibold text-npst-ink leading-[1.05] tracking-tight" data-testid="hero-title">
              National Public<br />Service Trust
            </h1>
            <p className="mt-8 font-serif text-2xl sm:text-3xl text-npst-navy leading-snug max-w-3xl" data-testid="hero-tagline">
              {s.tagline || "Public Interest. Institutional Accountability. Constitutional Governance."}
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/media" data-testid="cta-explore-work" className="inline-flex items-center gap-2 px-7 py-3.5 bg-npst-navy text-white text-sm font-semibold uppercase tracking-[0.1em] rounded-sm hover:bg-npst-navyDeep transition-colors">
                Explore Our Work <ArrowRight size={16} />
              </Link>
              <Link to="/pil" data-testid="cta-pils" className="inline-flex items-center gap-2 px-7 py-3.5 border border-npst-navy text-npst-navy text-sm font-semibold uppercase tracking-[0.1em] rounded-sm hover:bg-npst-navy hover:text-white transition-colors">
                Public Interest Litigations
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* AS SEEN IN */}
      <AsSeenIn />

      {/* INTRODUCTION */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-3"><div className="label-eyebrow text-npst-gold">About the Trust</div></div>
        <div className="lg:col-span-9">
          <p className="font-serif text-2xl sm:text-3xl text-npst-ink leading-relaxed">{s.intro}</p>
          <Link to="/about" className="inline-flex items-center gap-2 mt-8 text-sm font-semibold uppercase tracking-[0.12em] text-npst-navy link-underline" data-testid="about-trust-link">
            About the Trust <ArrowRight size={15} />
          </Link>
        </div>
      </section>

      <LensStrip issues={issues} />

      {/* AREAS OF WORK — derived from Under the Lens categories */}
      <DomainsSection issues={issues} />

      {/* PILs */}
      {home?.pils?.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="flex items-end justify-between mb-8">
            <SectionTitle eyebrow="Public Interest Litigations" title="Selected Matters" />
            <Link to="/pil" className="hidden sm:inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.12em] text-npst-navy link-underline">View Archive <ArrowRight size={15} /></Link>
          </div>
          <div>{home.pils.map((p, i) => <PilRow key={p.id} pil={p} index={i} />)}</div>
        </section>
      )}

      {/* MEDIA */}
      {home?.press?.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="flex items-end justify-between mb-10">
            <SectionTitle eyebrow="Media" title="Latest Press Releases" />
            <Link to="/media" className="hidden sm:inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.12em] text-npst-navy link-underline">All Media <ArrowRight size={15} /></Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-npst-line border border-npst-line rounded-md overflow-hidden">
            {home.press.map((p) => (
              <Link key={p.id} to={`/media/${p.slug}`} className="group bg-npst-bg p-7 hover:bg-white transition-colors" data-testid="home-press-item">
                <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.14em] text-npst-gold font-semibold mb-3">
                  <span>{p.publish_date}</span><span className="text-npst-line">·</span><span>{p.category}</span>
                </div>
                <div className="font-serif text-xl text-npst-ink group-hover:text-npst-navy transition-colors leading-snug mb-3">{p.headline}</div>
                <p className="text-sm text-npst-inkSoft leading-relaxed line-clamp-3">{p.excerpt}</p>
                <span className="inline-flex items-center gap-1.5 mt-4 text-xs uppercase tracking-[0.12em] text-npst-navy font-semibold">Read More <ArrowRight size={13} /></span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* IN THE NEWS */}
      {home?.coverage?.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
          <div className="flex items-end justify-between mb-8">
            <SectionTitle eyebrow="In the News" title="Media Coverage" />
            <Link to="/media#in-the-news" className="hidden sm:inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.12em] text-npst-navy link-underline">All Coverage <ArrowRight size={15} /></Link>
          </div>
          <CoverageList items={home.coverage} />
        </section>
      )}

      {/* STATS */}
      {statItems.length > 0 && <ImpactDashboard items={statItems} />}
    </div>
  );
}
